import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckSquare, CheckCircle2, CircleOff, Download, Trash2 } from 'lucide-react';
import { SearchIcon, PlusIcon, ChevronLeftIcon } from '@/components/icons';
import { useFilterVideos, useDeleteVideo, useUpdateVideoStatus, usePublishedStats, useDraftStats } from '@/lib/api/hooks/video';
import { useFolder } from '@/lib/api/hooks/folder';
import { useDebounce } from '@/hooks/helper-function';
import type { VideoItem } from '@/lib/api/types';
import { showToast } from '@/lib/toast';
import { Loader, Tab, ActionDropdown, DeleteModal, PublishModal, Pagination, Checkbox, type TabItem, type DropdownAction } from '@/components/ui';
import { useQueryClient } from '@tanstack/react-query';
import VideoModal from '@/pages/multimedia/videos/modals/VideoModal';
import VideoPlayerModal from '@/pages/multimedia/videos/modals/VideoPlayerModal';
import { handleDownloadFromUrl as downloadFileFromUrl } from '@/utils/helpers/handleDownloadFromUrl';

type TabType = 'published' | 'drafts';

const getYouTubeVideoId = (url: string): string | null => {
    const watchMatch = url.match(/(?:youtube\.com\/watch\?v=)([^&?/\n]+)/);
    if (watchMatch?.[1]) return watchMatch[1];

    const shortMatch = url.match(/(?:youtu\.be\/)([^&?/\n]+)/);
    if (shortMatch?.[1]) return shortMatch[1];

    const embedMatch = url.match(/(?:youtube\.com\/embed\/)([^&?/\n]+)/);
    if (embedMatch?.[1]) return embedMatch[1];

    return null;
};

const getVideoThumbnail = (video: VideoItem): string => {
    if (video.thumbnailUrl) return video.thumbnailUrl;

    const youtubeId = getYouTubeVideoId(video.embeddedLink || '');
    if (youtubeId) return `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;

    return '';
};

const getStatusPillClass = (status: string) => {
    if (status === 'PUBLISH') return 'bg-[#2F855A] text-white';
    if (status === 'DRAFT') return 'bg-[#ED8936] text-white';
    return 'bg-gray-700 text-white';
};

const getFormattedDateTime = (dateValue?: string) => {
    if (!dateValue) return { date: 'N/A', time: 'N/A' };
    const parsed = new Date(dateValue);
    if (Number.isNaN(parsed.getTime())) return { date: 'N/A', time: 'N/A' };

    return {
        date: parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        time: parsed.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
    };
};

const VideoCard: React.FC<{
    video: VideoItem;
    isSelected: boolean;
    onPlay: (video: VideoItem) => void;
    onToggleSelect: (videoId: string) => void;
    getDropdownActions: (video: VideoItem) => DropdownAction[];
}> = ({ video, isSelected, onPlay, onToggleSelect, getDropdownActions }) => {
    const thumbnailUrl = getVideoThumbnail(video);
    const { date, time } = getFormattedDateTime(video.createdAt);

    return (
        <div
            className={`group relative overflow-visible rounded-2xl border bg-white shadow-sm transition ${
                isSelected ? 'border-[#2F855A] ring-1 ring-[#2F855A]/40' : 'border-gray-200'
            }`}
        >
            <div className="relative cursor-pointer overflow-hidden rounded-t-2xl" onClick={() => onPlay(video)}>
                <button
                    className="absolute left-3 top-3 z-10"
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                >
                    <Checkbox
                        checked={isSelected}
                        onChange={() => onToggleSelect(video.videoUploadId)}
                        ariaLabel={isSelected ? 'Deselect video' : 'Select video'}
                    />
                </button>

                {thumbnailUrl ? (
                    <img src={thumbnailUrl} alt={video.title} className="h-48 2xl:h-56 w-full object-cover" />
                ) : (
                    <div className="flex h-48 2xl:h-56 w-full items-center justify-center bg-gray-100">
                        <div className="w-16 h-16 bg-wine-100 rounded-full flex items-center justify-center">
                            <div className="w-0 h-0 border-t-8 border-t-transparent border-l-12 border-l-white border-b-8 border-b-transparent ml-1"></div>
                        </div>
                    </div>
                )}

                <div className={`absolute bottom-3 left-3 inline-flex items-center rounded-md px-2.5 py-1 text-xs font-semibold uppercase tracking-wide ${getStatusPillClass(video.status)}`}>
                    {video.status.toLowerCase() === 'publish' ? 'Published' : video.status.toLowerCase() === 'draft' ? 'Draft' : 'Unknown'}
                </div>

                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <img src="/yt.png" alt="Play" className="w-8 h-8" />
                </div>
            </div>

            <div className="flex items-end justify-between gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                    <p className="line-clamp-1 text-lg leading-tight font-semibold text-gray-900">{video.title}</p>
                    <p className="mt-1 text-sm text-gray-500">{`${date}  •  ${time}`}</p>
                </div>
                <div className="shrink-0 relative z-20">
                    <ActionDropdown
                        id={video.videoUploadId}
                        actions={getDropdownActions(video)}
                    />
                </div>
            </div>
        </div>
    );
};

const VideoList: React.FC = () => {
    const { folderId } = useParams<{ folderId: string }>();
    const resolvedFolderId = folderId ?? '';
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState<TabType>('published');
    const [showVideoModal, setShowVideoModal] = useState(false);
    const [editingVideo, setEditingVideo] = useState<VideoItem | null>(null);
    const [playingVideo, setPlayingVideo] = useState<VideoItem | null>(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [publishModalOpen, setPublishModalOpen] = useState(false);
    const [selectedVideoForDelete, setSelectedVideoForDelete] = useState<VideoItem | null>(null);
    const [selectedVideoForStatusUpdate, setSelectedVideoForStatusUpdate] = useState<{ id: string; title: string; status: 'PUBLISH' | 'DRAFT' } | null>(null);
    const [selectedVideoIds, setSelectedVideoIds] = useState<string[]>([]);
    const [isBulkProcessing, setIsBulkProcessing] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 16;
    const debouncedSearch = useDebounce(searchValue, 500);

    // Fetch folder info
    const { data: folderData, isLoading: folderLoading } = useFolder(resolvedFolderId);
    const folderName = folderData?.data?.folderName || 'Loading...';


    // Fetch videos for this folder with pagination and status filter
    const { data: videosData, isLoading: videosLoading } = useFilterVideos(
        { page: currentPage, size: pageSize },
        { folderId: resolvedFolderId, status: activeTab === 'published' ? 'PUBLISH' : 'DRAFT' }
    );

    // Fetch tab counts from dedicated stats endpoints
    const { data: publishedStatsData } = usePublishedStats();
    const { data: draftStatsData } = useDraftStats();



    const deleteVideoMutation = useDeleteVideo();
    const updateVideoStatusMutation = useUpdateVideoStatus();

    // Flatten videos from nested folder/responseList structure
    const videos: VideoItem[] = useMemo(() => {
        const folders = videosData?.data?.data || [];


        return folders.flatMap((folder: { responseList?: VideoItem[] }) => folder.responseList || []);
    }, [videosData]);

    const totalPages = videosData?.data?.totalPage || 1;
    const selectedCount = selectedVideoIds.length;

    // Filter by search locally
    const filteredVideos = useMemo(() => {
        if (!debouncedSearch) return videos;
        return videos.filter((v: VideoItem) =>
            v.title.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
            v.description?.toLowerCase().includes(debouncedSearch.toLowerCase())
        );
    }, [videos, debouncedSearch]);

    useEffect(() => {
        const visibleVideoIds = new Set(filteredVideos.map((video) => video.videoUploadId));
        setSelectedVideoIds((prev) => prev.filter((id) => visibleVideoIds.has(id)));
    }, [filteredVideos]);

    useEffect(() => {
        setSelectedVideoIds([]);
        setIsBulkProcessing(false);
    }, [activeTab]);

    if (!folderId) {
        return (
            <div className="text-center p-8 bg-white rounded-lg">
                <h2 className="text-xl font-bold">Folder ID missing</h2>
                <button onClick={() => navigate('/admin/videos')} className="mt-4 text-[#047857] hover:underline">
                    ← Back to Video Folders
                </button>
            </div>
        );
    }

    const handleAddVideo = () => {
        setEditingVideo(null);
        setShowVideoModal(true);
    };

    const handleEditVideo = (video: VideoItem) => {
        setEditingVideo(video);
        setShowVideoModal(true);
    };

    const handlePlayVideo = (video: VideoItem) => {
        setPlayingVideo(video);
    };

    const handleDeleteVideo = (video: VideoItem) => {
        setSelectedVideoForDelete(video);
        setDeleteModalOpen(true);
    };

    const confirmDelete = () => {
        if (!selectedVideoForDelete) return;

        deleteVideoMutation.mutate(selectedVideoForDelete.videoUploadId, {
            onSuccess: (response) => {
                if (response.success) {
                    showToast.success('Video deleted successfully');
                    setDeleteModalOpen(false);
                    setSelectedVideoForDelete(null);
                    queryClient.invalidateQueries({ queryKey: ['videos'] });
                } else {
                    showToast.error('Failed to delete video');
                }
            },
            onError: (error: Error) => {
                showToast.error('Failed to delete: ' + (error.message || 'Unknown error'));
            }
        });
    };

    const handlePublishVideo = (video: VideoItem) => {
        setSelectedVideoForStatusUpdate({ id: video.videoUploadId, title: video.title, status: 'PUBLISH' });
        setPublishModalOpen(true);
    };

    const handleUnpublishVideo = (video: VideoItem) => {
        setSelectedVideoForStatusUpdate({ id: video.videoUploadId, title: video.title, status: 'DRAFT' });
        setPublishModalOpen(true);
    };

    const confirmStatusUpdate = () => {
        if (!selectedVideoForStatusUpdate) return;

        const newStatus = selectedVideoForStatusUpdate.status;

        updateVideoStatusMutation.mutate(
            {
                videoId: selectedVideoForStatusUpdate.id,
                data: { status: newStatus }
            },
            {
                onSuccess: () => {
                    showToast.success(
                        newStatus === 'PUBLISH'
                            ? 'Video published successfully!'
                            : 'Video unpublished successfully!'
                    );
                    setPublishModalOpen(false);
                    setSelectedVideoForStatusUpdate(null);
                    queryClient.invalidateQueries({ queryKey: ['videos'] });
                },
                onError: (error: Error) => {
                    showToast.error('Failed to update status: ' + (error.message || 'Unknown error'));
                }
            }
        );
    };

    const getDropdownActions = (video: VideoItem): DropdownAction[] => {
        const actions: DropdownAction[] = [
            {
                label: 'Edit Video',
                onClick: (e) => {
                    e.stopPropagation();
                    handleEditVideo(video);
                }
            },
            {
                label: video.status === 'PUBLISH' ? 'Unpublish' : 'Publish',
                onClick: (e) => {
                    e.stopPropagation();
                    if (video.status === 'PUBLISH') {
                        handleUnpublishVideo(video);
                    } else {
                        handlePublishVideo(video);
                    }
                },
                disabled: updateVideoStatusMutation.isPending
            }
        ];

        if (video.status === 'DRAFT') {
            actions.push({
                label: 'Delete',
                onClick: (e) => {
                    e.stopPropagation();
                    handleDeleteVideo(video);
                },
                variant: 'danger' as const,
                disabled: deleteVideoMutation.isPending
            });
        }

        return actions;
    };

    const toggleVideoSelection = (videoId: string) => {
        setSelectedVideoIds((prev) =>
            prev.includes(videoId) ? prev.filter((id) => id !== videoId) : [...prev, videoId]
        );
    };

    const clearSelection = () => {
        setSelectedVideoIds([]);
    };

    const updateVideoStatusAsync = (videoId: string, status: 'PUBLISH' | 'DRAFT') => {
        return new Promise<void>((resolve, reject) => {
            updateVideoStatusMutation.mutate(
                { videoId, data: { status } },
                {
                    onSuccess: () => resolve(),
                    onError: reject,
                }
            );
        });
    };

    const deleteVideoAsync = (videoId: string) => {
        return new Promise<void>((resolve, reject) => {
            deleteVideoMutation.mutate(videoId, {
                onSuccess: (response) => {
                    if (response?.success) {
                        resolve();
                    } else {
                        reject(new Error('Failed to delete one or more videos'));
                    }
                },
                onError: reject,
            });
        });
    };

    const runBulkStatusUpdate = async (status: 'PUBLISH' | 'DRAFT') => {
        if (!selectedVideoIds.length || isBulkProcessing) return;
        setIsBulkProcessing(true);

        try {
            const results = await Promise.allSettled(
                selectedVideoIds.map((videoId) => updateVideoStatusAsync(videoId, status))
            );

            const successfulCount = results.filter((result) => result.status === 'fulfilled').length;
            const failedCount = results.length - successfulCount;

            if (successfulCount > 0) {
                showToast.success(
                    status === 'PUBLISH'
                        ? `${successfulCount} selected videos published`
                        : `${successfulCount} selected videos unpublished`
                );
                clearSelection();
            }

            if (failedCount > 0) {
                showToast.error(`Failed to update ${failedCount} ${failedCount === 1 ? 'video' : 'videos'}`);
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Bulk status update failed';
            showToast.error(message);
        } finally {
            setIsBulkProcessing(false);
        }
    };

    const runBulkDelete = async () => {
        if (!selectedVideoIds.length || isBulkProcessing) return;
        setIsBulkProcessing(true);

        try {
            const results = await Promise.allSettled(selectedVideoIds.map((videoId) => deleteVideoAsync(videoId)));
            const successfulCount = results.filter((result) => result.status === 'fulfilled').length;
            const failedCount = results.length - successfulCount;

            if (successfulCount > 0) {
                showToast.success(`${successfulCount} selected videos deleted`);
                clearSelection();
            }

            if (failedCount > 0) {
                showToast.error(`Failed to delete ${failedCount} ${failedCount === 1 ? 'video' : 'videos'}`);
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Bulk delete failed';
            showToast.error(message);
        } finally {
            setIsBulkProcessing(false);
        }
    };

    const runBulkDownload = async () => {
        if (!selectedVideoIds.length || isBulkProcessing) return;

        const selectedVideos = filteredVideos.filter((video) => selectedVideoIds.includes(video.videoUploadId));
        const downloadableVideos = selectedVideos.filter((video) => Boolean(video.embeddedLink));

        if (!downloadableVideos.length) {
            showToast.error('No downloadable files found for selected videos');
            return;
        }

        setIsBulkProcessing(true);
        try {
            await Promise.all(
                downloadableVideos.map((video) =>
                    downloadFileFromUrl({
                        url: video.embeddedLink,
                        fileName: video.title || 'video-file',
                    })
                )
            );
            showToast.success(`${downloadableVideos.length} selected video files downloaded`);
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Bulk download failed';
            showToast.error(message);
        } finally {
            setIsBulkProcessing(false);
        }
    };

    const handleVideoSaved = () => {
        queryClient.invalidateQueries({ queryKey: ['videos'] });
    };

    const tabItems: TabItem<TabType>[] = [
        { key: 'published', label: 'Published', count: publishedStatsData?.data ?? 0 },
        { key: 'drafts', label: 'Drafts', count: draftStatsData?.data ?? 0 }
    ];

    return (
        <>
            <div className="space-y-6">
                {/* Breadcrumb and Tabs */}
                <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm">
                        <button
                            onClick={() => navigate('/admin/videos')}
                            className="text-[#047857] hover:underline font-medium"
                        >
                            Videos
                        </button>
                        <ChevronLeftIcon className="w-4 h-4 rotate-180 text-gray-400" />
                        <span className="text-gray-700 font-medium">{folderName}</span>
                    </div>
                    <Tab
                        tabs={tabItems}
                        activeTab={activeTab}
                        onTabChange={setActiveTab}
                    />
                </div>

                {/* Search and Actions row */}
                <div className="flex items-center justify-between gap-4">
                    <div className="relative w-80">
                        <input
                            type="text"
                            placeholder="Search videos..."
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-0 focus:outline-none focus:border-transparent"
                        />
                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                            <SearchIcon className="w-4 h-4 text-gray-400" />
                        </div>
                    </div>
                    <button
                        onClick={handleAddVideo}
                        className="bg-[#047857] hover:bg-[#059669] text-white font-semibold py-2.5 px-4 rounded-lg text-sm flex items-center gap-2"
                    >
                        <PlusIcon className="w-4 h-4" />
                        Add Video
                    </button>
                </div>

                {/* Content */}
                <div className="p-5">
                    {videosLoading || folderLoading ? (
                        <Loader size="lg" variant="wine" message="Loading videos..." />
                    ) : filteredVideos.length > 0 ? (
                        <>
                            {selectedCount > 0 && (
                                <div className="mb-4 flex items-center overflow-hidden rounded-xl border border-gray-200 bg-white">
                                    <div className="flex items-center gap-2 border-r border-gray-200 px-4 py-3 text-sm font-semibold text-gray-700">
                                        <CheckSquare className="h-4 w-4 text-[#0F8A47]" />
                                        {selectedCount} selected
                                    </div>

                                    {activeTab === 'drafts' && (
                                        <button
                                            type="button"
                                            onClick={() => runBulkStatusUpdate('PUBLISH')}
                                            disabled={isBulkProcessing || updateVideoStatusMutation.isPending}
                                            className="inline-flex items-center justify-center gap-2 border-r border-gray-200 px-3 py-3 text-sm font-semibold text-[#0F8A47] disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            <CheckCircle2 className="h-4 w-4" /> Publish
                                        </button>
                                    )}

                                    {activeTab === 'published' && (
                                        <button
                                            type="button"
                                            onClick={() => runBulkStatusUpdate('DRAFT')}
                                            disabled={isBulkProcessing || updateVideoStatusMutation.isPending}
                                            className="inline-flex items-center justify-center gap-2 border-r border-gray-200 px-3 py-3 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            <CircleOff className="h-4 w-4" /> Unpublish
                                        </button>
                                    )}

                                    <button
                                        type="button"
                                        onClick={runBulkDownload}
                                        disabled={isBulkProcessing}
                                        className="inline-flex items-center justify-center gap-2 border-r border-gray-200 px-3 py-3 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        <Download className="h-4 w-4" /> Download
                                    </button>

                                    {activeTab === 'drafts' && (
                                        <button
                                            type="button"
                                            onClick={runBulkDelete}
                                            disabled={isBulkProcessing || deleteVideoMutation.isPending}
                                            className="inline-flex items-center justify-center gap-2 border-r border-gray-200 px-3 py-3 text-sm font-semibold text-[#E53E3E] disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            <Trash2 className="h-4 w-4" /> Delete
                                        </button>
                                    )}

                                    <button
                                        type="button"
                                        onClick={clearSelection}
                                        disabled={isBulkProcessing}
                                        className="ml-auto px-4 py-3 text-sm font-semibold text-[#0F8A47] disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        Clear
                                    </button>
                                </div>
                            )}

                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-5">
                                {filteredVideos.map((video: VideoItem) => (
                                    <VideoCard
                                        key={video.videoUploadId}
                                        video={video}
                                        isSelected={selectedVideoIds.includes(video.videoUploadId)}
                                        onPlay={handlePlayVideo}
                                        onToggleSelect={toggleVideoSelection}
                                        getDropdownActions={getDropdownActions}
                                    />
                                ))}
                            </div>

                            {/* Pagination */}
                        
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={setCurrentPage}
                                    className="mt-6"
                                />
                    
                        </>
                    ) : (
                        <div className="text-center py-20">
                            <img src="/No-post-icon.png" alt="No videos" className="w-20 h-20 object-contain mx-auto" />
                            <h3 className="mt-4 font-semibold text-gray-800">
                                {searchValue ? 'No videos found' : 'No Videos Yet'}
                            </h3>
                            <p className="mt-1 text-sm text-gray-500">
                                {searchValue
                                    ? `No videos match "${searchValue}"`
                                    : 'Add videos to this folder'}
                            </p>
                            {!searchValue && (
                                <button
                                    onClick={handleAddVideo}
                                    className="mt-6 bg-[#047857] hover:bg-[#059669] text-white font-semibold py-2.5 px-5 rounded-lg transition-colors text-sm inline-flex items-center gap-2"
                                >
                                    <PlusIcon className="w-4 h-4" />
                                    Add Video
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Video Modal */}
            <VideoModal
                isOpen={showVideoModal}
                onClose={() => setShowVideoModal(false)}
                video={editingVideo}
                folderId={folderId}
                onSave={handleVideoSaved}
            />

            {/* Video Player Modal */}
            {playingVideo && (
                <VideoPlayerModal
                    isOpen={!!playingVideo}
                    onClose={() => setPlayingVideo(null)}
                    video={playingVideo}
                />
            )}

            {/* Delete Modal */}
            <DeleteModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                title="Delete Video"
                subtitle="Are you sure you want to delete this video?"
                itemName={selectedVideoForDelete?.title}
                warningText="This action cannot be undone."
                isLoading={deleteVideoMutation.isPending}
            />

            {/* Publish Modal */}
            <PublishModal
                isOpen={publishModalOpen}
                onClose={() => setPublishModalOpen(false)}
                onConfirm={confirmStatusUpdate}
                title={selectedVideoForStatusUpdate?.status === 'PUBLISH' ? 'Publish Video?' : 'Unpublish Video?'}
                subtitle={`Are you sure you want to ${selectedVideoForStatusUpdate?.status === 'PUBLISH' ? 'publish' : 'unpublish'} this video?`}
                itemName={selectedVideoForStatusUpdate?.title}
                infoText={selectedVideoForStatusUpdate?.status === 'PUBLISH'
                    ? 'This video will be visible to the public once published.'
                    : 'This video will no longer be visible to the public.'}
                itemType="Video"
                isLoading={updateVideoStatusMutation.isPending}
            />
        </>
    );
};

export default VideoList;
